import { useCallback, useState } from "react"
import { Sl1Finding, Sl1ImportReport } from "shared/sl1Import"
import { SL1_IMPORT_SCHEMA_VERSION } from "shared/sl1ImportData"
import {
  Sl1ImportJobDto,
  useLazyGetSl1ImportObjectUrlQuery,
} from "src/features/things/sl1ImportApi"

/**
 * Where the report detail gets its body (LOCKER-382 · children C + D).
 * ====================================================================
 *
 * TWO sources, and the job record says which one applies:
 *
 *  - **S3**, when the run was published. Two hops by design — the API returns a
 *    presigned URL validated against the run's own manifest, then the browser
 *    reads S3 directly. A 6 MB part would exceed API Gateway's response limit,
 *    and proxying it would put customer rows through a Lambda with no reason to
 *    hold them.
 *  - **A local file**, when it was not (`--no-upload`, or an upload that could
 *    not reach the bucket). `reportIsLocal` is what distinguishes them; the two
 *    cannot be told apart from the key.
 *
 * ## Parts are loaded ONE AT A TIME, and the count is shown
 *
 * `findings` for a real provider is several 6 MB parts. Fetching them all up
 * front would block the screen on the whole report; fetching on demand lets the
 * first part render while the rest waits for the reader to ask. `partsLoaded` /
 * `partsTotal` is on the screen so a partially-loaded report can never read as a
 * complete one — the same rule the reservation-sync loader follows, and the
 * reason it exists.
 *
 * ## Why a loaded body is still validated
 *
 * From a file it is whatever the operator picked; from S3 it is whatever the
 * manifest pointed at. A mis-picked file — last month's report, a drift report, a
 * package.json — must fail with a sentence naming what was wrong, not with
 * `Cannot read properties of undefined` three components deeper. And loading run
 * A's report onto run B's page is the one mistake that produces a completely
 * plausible, completely wrong screen.
 */

export type Sl1LoadedFindings = {
  rows: Sl1Finding[]
  partsLoaded: number
  partsTotal: number
  bytesLoaded: number
  loading: boolean
  error?: string
}

export type Sl1ReportSource = {
  report?: Sl1ImportReport
  findings: Sl1LoadedFindings
  error?: string
  loading: boolean
  /** True when the only way in is a local file — i.e. the run was not published. */
  localOnly: boolean
  loadFile: (file: File) => Promise<void>
  /** Fetch the report head from S3. No-op when `localOnly`. */
  loadRemote: () => Promise<void>
  /** Fetch the next unloaded `findings` part. Idempotent while in flight. */
  loadMoreFindings: () => Promise<void>
  clear: () => void
}

const emptyFindings = (partsTotal: number): Sl1LoadedFindings => ({
  rows: [],
  partsLoaded: 0,
  partsTotal,
  bytesLoaded: 0,
  loading: false,
})

/** Everything the detail screen actually dereferences. Checked, not assumed. */
export const validateReport = (
  value: unknown,
  expectedJobId: string | undefined,
): { report?: Sl1ImportReport; error?: string } => {
  const r = value as Partial<Sl1ImportReport> | null
  if (!r || typeof r !== "object") return { error: "sl1ReportNotJson" }
  if (typeof r.schemaVersion !== "number") return { error: "sl1ReportNotAReport" }
  if (r.schemaVersion !== SL1_IMPORT_SCHEMA_VERSION) {
    // A newer report on an older console renders fields it does not understand as
    // blanks, which reads as "no anomalies" rather than "cannot read this".
    return { error: "sl1ReportSchemaMismatch" }
  }
  if (!Array.isArray(r.anomalies)) return { error: "sl1ReportNotAReport" }
  if (expectedJobId && r.job?.jobId && r.job.jobId !== expectedJobId) {
    // The dangerous one: a valid report for a DIFFERENT run renders perfectly and
    // is entirely wrong.
    return { error: "sl1ReportWrongJob" }
  }
  return { report: r as Sl1ImportReport }
}

export const useSl1ReportSource = (
  job: Sl1ImportJobDto | undefined,
): Sl1ReportSource => {
  const [fetchUrl] = useLazyGetSl1ImportObjectUrlQuery()
  const [report, setReport] = useState<Sl1ImportReport | undefined>()
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(false)

  const findingParts = (job?.objects ?? [])
    .filter((o) => o.kind === "findings")
    .sort((a, b) => a.rowFrom - b.rowFrom)
  const [findings, setFindings] = useState<Sl1LoadedFindings>(() =>
    emptyFindings(0),
  )

  // Local when the record says so, and ALSO when the run stored no manifest —
  // a fetch button that cannot work is worse than asking for the file.
  const localOnly = job?.reportIsLocal !== false || !(job?.objects?.length)

  /** Presign, then read S3 directly. Returns the parsed body of one object. */
  const fetchObject = useCallback(
    async (key: string): Promise<{ rows?: unknown[] } & Record<string, any>> => {
      if (!job) throw new Error("no job")
      const res = await fetchUrl({ jobId: job.jobId, key }).unwrap()
      const body = await fetch(res.data.url)
      if (!body.ok)
        throw new Error(
          `S3 returned ${body.status} for ${key} — the presigned URL may have expired`,
        )
      return await body.json()
    },
    [fetchUrl, job],
  )

  const accept = useCallback(
    (value: unknown) => {
      const { report: ok, error: err } = validateReport(value, job?.jobId)
      setReport(ok)
      setError(err)
      return !!ok
    },
    [job?.jobId],
  )

  const loadFile = useCallback(
    async (file: File) => {
      setLoading(true)
      setError(undefined)
      try {
        const text = await file.text()
        let parsed: unknown
        try {
          parsed = JSON.parse(text)
        } catch {
          setReport(undefined)
          setError("sl1ReportNotJson")
          return
        }
        // A local file is the WHOLE report, findings included — it is what the
        // script wrote, not a part.
        if (accept(parsed)) {
          const whole = parsed as Sl1ImportReport
          setFindings({
            rows: whole.findings ?? [],
            partsLoaded: 1,
            partsTotal: 1,
            bytesLoaded: text.length,
            loading: false,
          })
        }
      } finally {
        setLoading(false)
      }
    },
    [accept],
  )

  const loadRemote = useCallback(async () => {
    if (!job || localOnly) return
    const head = (job.objects ?? []).find((o) => o.kind === "report")
    if (!head) {
      setError("sl1ReportNoRemoteHead")
      return
    }
    setLoading(true)
    setError(undefined)
    try {
      const body = await fetchObject(head.key)
      // The head object stores the report WITHOUT findings, as a single row.
      accept(body.rows?.[0])
      setFindings(emptyFindings(findingParts.length))
    } catch (e: any) {
      setReport(undefined)
      setError(e?.message ?? "sl1ReportFetchFailed")
    } finally {
      setLoading(false)
    }
  }, [accept, fetchObject, findingParts.length, job, localOnly])

  const loadMoreFindings = useCallback(async () => {
    if (!job || localOnly) return
    setFindings((f) => {
      if (f.loading || f.partsLoaded >= findingParts.length) return f
      return { ...f, partsTotal: findingParts.length, loading: true, error: undefined }
    })
    const next = findingParts[findings.partsLoaded]
    if (!next) return
    try {
      const body = await fetchObject(next.key)
      setFindings((prev) => ({
        rows: [...prev.rows, ...((body.rows ?? []) as Sl1Finding[])],
        partsLoaded: prev.partsLoaded + 1,
        partsTotal: findingParts.length,
        bytesLoaded: prev.bytesLoaded + next.bytes,
        loading: false,
      }))
    } catch (e: any) {
      setFindings((prev) => ({
        ...prev,
        loading: false,
        error: e?.message ?? "sl1ReportFetchFailed",
      }))
    }
  }, [fetchObject, findingParts, findings.partsLoaded, job, localOnly])

  const clear = useCallback(() => {
    setReport(undefined)
    setError(undefined)
    setFindings(emptyFindings(findingParts.length))
  }, [findingParts.length])

  return {
    report,
    findings,
    error,
    loading,
    localOnly,
    loadFile,
    loadRemote,
    loadMoreFindings,
    clear,
  }
}
